import { transactionStatus } from 'Models/Enums';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { TransactionColumnKeys } from './TransactionColumnKeys';

export interface ColumnConfig {
	key: string;
	label: string;
	visible: boolean;
	locked?: boolean;
}

interface ColumnVisibility {
	[key: string]: boolean;
}

export type transactionListStatus = 'POS_PAYMENT' | transactionStatus;

export type transactionTypeFilter =
	| 'FERRY_BOOKING'
	| 'EVENT_BOOKING'
	| 'PARKING'
	| 'VOUCHER_PURCHASE'
	| 'ANYTIME_TICKET'
	| 'POS'
	| 'CHARTER';

export type transactionActionFilter = 'CREATION' | 'ALTERATION' | 'CANCELLATION';

export interface StaffTransactionsFilters {
	searchString?: string;
	startDate?: Date;
	endDate?: Date;
	filterByCreatedDate: boolean;
	statusFilters: transactionListStatus[];
	locationFilters: string[];
	createdByFilters: string[];
	typeFilters: transactionTypeFilter[];
	actionFilters: transactionActionFilter[];
	orderBy: string;
	orderByDescending: boolean;
	pageNo: number;
}

interface StaffTransactionsState {
	// Current filters
	filters: StaffTransactionsFilters;

	// UI state
	selectedStatuses: transactionListStatus[];
	selectedLocations: string[];
	selectedCreatedBy: string[];
	selectedTypes: transactionTypeFilter[];
	selectedActions: transactionActionFilter[];
	filterByCreatedDate: boolean;

	// Context tracking for smart resets
	currentContext: string | null;

	// Column visibility
	columnVisibility: ColumnVisibility;
	visibleColumns: ColumnConfig[];

	// Selection state for export
	selectedTransactionIds: Set<string>;

	// Export state
	isExporting: boolean;

	// Actions
	setFilters: (filters: StaffTransactionsFilters) => void;
	updateFilters: (partialFilters: Partial<StaffTransactionsFilters>) => void;
	resetFilters: () => void;
	setSelectedStatuses: (statuses: transactionListStatus[]) => void;
	setSelectedLocations: (locations: string[]) => void;
	setSelectedCreatedBy: (createdBy: string[]) => void;
	setSelectedTypes: (types: transactionTypeFilter[]) => void;
	setSelectedActions: (actions: transactionActionFilter[]) => void;
	setFilterByCreatedDate: (filterByCreatedDate: boolean) => void;
	setSearchString: (searchString?: string) => void;
	setContext: (context: string | null) => void;
	setVisibleColumns: (columns: ColumnConfig[]) => void;
	toggleColumn: (key: string) => void;
	setSelectedTransactionIds: (ids: Set<string>) => void;
	setIsExporting: (isExporting: boolean) => void;
}

const defaultSelectedStatuses: transactionListStatus[] = [
	'COMPLETED',
	'POS_PAYMENT',
	'PENDING_INVOICE',
	'INVOICED',
	'PENDING_PAYMENT',
	'EFTPOS',
	'CASH',
	'IN_PROGRESS',
];

const defaultSelectedLocations: string[] = ['CHECK_IN', 'BOOKING_WIZARD'];
const defaultSelectedCreatedBy: string[] = ['Staff', 'Customer', 'System'];
// Empty = no type filter = show everything (the backend only narrows when a non-empty subset is
// sent). Kept empty by default so the table shows all transactions — including historical ones of
// a now-disabled feature — while the filter UI only offers checkboxes for enabled types.
const defaultSelectedTypes: transactionTypeFilter[] = [];
// Empty = no action filter = show everything (same convention as the type filter).
const defaultSelectedActions: transactionActionFilter[] = [];

export const defaultFilters: StaffTransactionsFilters = {
	statusFilters: defaultSelectedStatuses,
	locationFilters: defaultSelectedLocations,
	createdByFilters: defaultSelectedCreatedBy,
	typeFilters: defaultSelectedTypes,
	actionFilters: defaultSelectedActions,
	orderBy: 'CREATED_DATE',
	orderByDescending: true,
	pageNo: 0,
	filterByCreatedDate: true,
	searchString: '',
};

// Default columns configuration
const getDefaultColumns = (): ColumnConfig[] => [
	{ key: TransactionColumnKeys.TRANSACTION_ID, label: 'Transaction ID', visible: true, locked: true },
	{ key: TransactionColumnKeys.CREATED_DATE, label: 'Created', visible: true },
	{ key: TransactionColumnKeys.CUSTOMER, label: 'Customer', visible: true },
	{ key: TransactionColumnKeys.BOOKINGS, label: 'References', visible: true },
	{ key: TransactionColumnKeys.ENTITY_TYPE, label: 'Type', visible: true, locked: true },
	{ key: TransactionColumnKeys.TYPE, label: 'Action', visible: true },
	{ key: TransactionColumnKeys.AMOUNT, label: 'Amount', visible: true },
	{ key: TransactionColumnKeys.STATUS, label: 'Status', visible: true },
	{ key: TransactionColumnKeys.CREATED_BY, label: 'Created By', visible: true },
];

// Helper to merge visibility with default columns
const buildColumns = (visibility: ColumnVisibility): ColumnConfig[] => {
	const defaults = getDefaultColumns();

	return defaults.map(col => ({
		...col,
		visible: visibility[col.key] ?? col.visible,
	}));
};

// Custom serializer to handle Date objects
const customStorage = {
	getItem: (name: string) => {
		const str = sessionStorage.getItem(name);
		if (!str) return null;

		const parsed = JSON.parse(str);

		// Convert date strings back to Date objects
		if (parsed.state?.filters?.startDate) {
			parsed.state.filters.startDate = new Date(parsed.state.filters.startDate);
		}
		if (parsed.state?.filters?.endDate) {
			parsed.state.filters.endDate = new Date(parsed.state.filters.endDate);
		}

		return str;
	},
	setItem: (name: string, value: string) => {
		sessionStorage.setItem(name, value);
	},
	removeItem: (name: string) => sessionStorage.removeItem(name),
};

export const useStaffTransactionsStore = create<StaffTransactionsState>()(
	persist(
		(set, get) => ({
			// Initial state
			filters: defaultFilters,
			selectedStatuses: defaultSelectedStatuses,
			selectedLocations: defaultSelectedLocations,
			selectedCreatedBy: defaultSelectedCreatedBy,
			selectedTypes: defaultSelectedTypes,
			selectedActions: defaultSelectedActions,
			filterByCreatedDate: true,
			currentContext: null,
			columnVisibility: {},
			visibleColumns: getDefaultColumns(),
			selectedTransactionIds: new Set<string>(),
			isExporting: false,

			// Actions
			setFilters: (filters: StaffTransactionsFilters) => {
				set({
					filters,
					selectedStatuses: filters.statusFilters,
					selectedLocations: filters.locationFilters,
					selectedCreatedBy: filters.createdByFilters,
					selectedTypes: filters.typeFilters,
					selectedActions: filters.actionFilters,
					filterByCreatedDate: filters.filterByCreatedDate,
				});
			},

			updateFilters: (partialFilters: Partial<StaffTransactionsFilters>) => {
				const currentFilters = get().filters;
				const newFilters = { ...currentFilters, ...partialFilters };

				set({
					filters: newFilters,
					// Sync UI state with filters
					selectedStatuses: newFilters.statusFilters,
					selectedLocations: newFilters.locationFilters,
					selectedCreatedBy: newFilters.createdByFilters,
					selectedTypes: newFilters.typeFilters,
					selectedActions: newFilters.actionFilters,
					filterByCreatedDate: newFilters.filterByCreatedDate,
				});
			},

			resetFilters: () => {
				set({
					filters: defaultFilters,
					selectedStatuses: defaultSelectedStatuses,
					selectedLocations: defaultSelectedLocations,
					selectedCreatedBy: defaultSelectedCreatedBy,
					selectedTypes: defaultSelectedTypes,
					selectedActions: defaultSelectedActions,
					filterByCreatedDate: defaultFilters.filterByCreatedDate,
				});
			},

			setContext: (currentContext: string | null) => {
				set({ currentContext });
			},

			setFilterByCreatedDate: (filterByCreatedDate: boolean) => {
				const currentFilters = get().filters;

				set({
					filterByCreatedDate,
					filters: {
						...currentFilters,
						filterByCreatedDate,
						pageNo: 0,
					},
				});
			},

			setSelectedStatuses: (statuses: transactionListStatus[]) => {
				const currentFilters = get().filters;

				set({
					selectedStatuses: statuses,
					filters: {
						...currentFilters,
						statusFilters: statuses,
						pageNo: 0,
					},
				});
			},

			setSelectedLocations: (locations: string[]) => {
				const currentFilters = get().filters;

				set({
					selectedLocations: locations,
					filters: {
						...currentFilters,
						locationFilters: locations,
						pageNo: 0,
					},
				});
			},

			setSelectedCreatedBy: (createdBy: string[]) => {
				const currentFilters = get().filters;

				set({
					selectedCreatedBy: createdBy,
					filters: {
						...currentFilters,
						createdByFilters: createdBy,
						pageNo: 0,
					},
				});
			},

			setSelectedTypes: (types: transactionTypeFilter[]) => {
				const currentFilters = get().filters;

				set({
					selectedTypes: types,
					filters: {
						...currentFilters,
						typeFilters: types,
						pageNo: 0,
					},
				});
			},

			setSelectedActions: (actions: transactionActionFilter[]) => {
				const currentFilters = get().filters;

				set({
					selectedActions: actions,
					filters: {
						...currentFilters,
						actionFilters: actions,
						pageNo: 0,
					},
				});
			},

			setSearchString: (searchString?: string) => {
				const currentFilters = get().filters;

				set({
					filters: {
						...currentFilters,
						searchString,
						pageNo: 0,
					},
				});
			},

			setVisibleColumns: (columns: ColumnConfig[]) => {
				const visibility: ColumnVisibility = {};
				columns.forEach(col => {
					visibility[col.key] = col.visible;
				});
				set({
					visibleColumns: columns,
					columnVisibility: visibility,
				});
			},

			toggleColumn: (key: string) => {
				const currentColumns = get().visibleColumns;
				const updatedColumns = currentColumns.map(col =>
					col.key === key && !col.locked ? { ...col, visible: !col.visible } : col,
				);

				const visibility: ColumnVisibility = {};
				updatedColumns.forEach(col => {
					visibility[col.key] = col.visible;
				});

				set({
					visibleColumns: updatedColumns,
					columnVisibility: visibility,
				});
			},

			setSelectedTransactionIds: (ids: Set<string>) => {
				set({ selectedTransactionIds: ids });
			},

			setIsExporting: (isExporting: boolean) => {
				set({ isExporting });
			},
		}),
		{
			// v2: bumped when the "Type" (entity) column + type filter were added, so stale
			// persisted state (missing typeFilters, pre-rename column visibility) is discarded
			// rather than merged and causing filter/column weirdness.
			name: 'staff-transactions-filters-v2',
			storage: createJSONStorage(() => customStorage),
			partialize: state => ({
				filters: state.filters,
				selectedStatuses: state.selectedStatuses,
				selectedLocations: state.selectedLocations,
				selectedCreatedBy: state.selectedCreatedBy,
				selectedTypes: state.selectedTypes,
				selectedActions: state.selectedActions,
				filterByCreatedDate: state.filterByCreatedDate,
				currentContext: state.currentContext,
				columnVisibility: state.columnVisibility,
			}),
			onRehydrateStorage: () => state => {
				if (state) {
					// Ensure all state is in sync after hydration
					if (state.filters) {
						state.selectedStatuses = state.filters.statusFilters || defaultSelectedStatuses;
						state.selectedLocations = state.filters.locationFilters || defaultSelectedLocations;
						state.selectedCreatedBy = state.filters.createdByFilters || defaultSelectedCreatedBy;
						state.filters.typeFilters = state.filters.typeFilters || defaultSelectedTypes;
						state.selectedTypes = state.filters.typeFilters;
						state.filters.actionFilters = state.filters.actionFilters || defaultSelectedActions;
						state.selectedActions = state.filters.actionFilters;
						state.filterByCreatedDate = state.filters.filterByCreatedDate ?? true;
					}
					// Rebuild columns with fresh configs and persisted visibility
					state.visibleColumns = buildColumns(state.columnVisibility || {});
				}
			},
		},
	),
);
